<template>
  <div class="max-w-7xl">
    <article class="card-style rounded-xl p-4 sm:p-6 group hover:shadow-lg hover:shadow-blue-500/5 transition-all">
      <!-- Mobile Layout -->
      <div class="md:hidden">
        <!-- Logo + Main Info -->
        <div class="flex gap-3 items-start">
          <div class="w-16 h-16 shrink-0 bg-gray-800/50 rounded-lg p-2">
            <img 
              :src="toolImage" 
              :alt="`${tool.name} logo`"
              class="w-full h-full object-contain"
              @error="onImageError"
              loading="eager"
              fetchpriority="high"
            />
          </div>
          <div class="flex-1 min-w-0">
            <h1 class="text-xl font-bold text-white">
              {{ tool.name }}
            </h1>
            <div class="flex items-center gap-3 mt-1 text-sm">
              <!-- Pricing Models -->
              <div class="flex items-center gap-1">
                <Icon icon="heroicons:currency-dollar" class="w-4 h-4 text-blue-400" aria-hidden="true" />
                <div class="flex flex-wrap items-center gap-x-2">
                  <span class="text-gray-300 text-sm capitalize">{{ tool.pricing_model || 'Free' }}</span>
                </div>
              </div>

              <div class="w-px h-4 bg-gray-700"></div>

              <!-- Platforms - only show if available -->
              <div v-if="tool.platforms && tool.platforms.length" class="flex items-center gap-1">
                <Icon icon="heroicons:computer-desktop" class="w-4 h-4 text-purple-400" aria-hidden="true" />
                <div class="flex flex-wrap items-center gap-x-2">
                  <span 
                    v-for="(platform, index) in tool.platforms.slice(0, 2)" 
                    :key="platform.id"
                    class="text-gray-300 text-sm"
                  >
                    {{ platform.name }}{{ index < Math.min(tool.platforms.length, 2) - 1 ? ',' : '' }}
                  </span>
                  <span v-if="tool.platforms.length > 2" class="text-gray-400 text-xs">+{{ tool.platforms.length - 2 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons (including Compare) -->
        <div class="flex items-center gap-2 mt-3">
          <a 
            :href="`/redirect/${tool.id}/tool_page/action`"
            target="_blank"
            rel="noopener"
            class="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white py-2.5 px-4 rounded-lg text-sm font-medium"
          >
            <Icon icon="heroicons:globe-alt" class="w-4 h-4" aria-hidden="true" />
            Visit Website
          </a>
          
          <!-- Compare Button -->
          <button 
            @click="toggleInComparison"
            :class="[
              'flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg transition-all text-sm',
              isInCompare ? 
                'bg-purple-600 text-white' : 
                'bg-gray-700 text-gray-200'
            ]"
            :title="isInCompare ? 'Remove from compare' : 'Add to compare'"
          >
            <Icon 
              icon="heroicons:square-2-stack" 
              class="w-4 h-4" 
              aria-hidden="true"
            />
            <span class="sr-only">{{ isInCompare ? 'Remove from compare' : 'Add to compare' }}</span>
          </button>
        </div>

        <!-- Description -->
        <p class="mt-3 text-sm text-gray-300 leading-relaxed">
          {{ tool.short_description }}
        </p>

        <!-- Additional Info Section - lazy loaded -->
        <div v-if="showAdditionalInfo" class="mt-3 border-t border-gray-800/50 pt-3">
          <!-- Icon Info List -->
          <div class="flex flex-col gap-2.5">
            <!-- Categories -->
            <div v-if="tool.use_cases && tool.use_cases.length" class="flex items-start gap-2">
              <Icon icon="heroicons:squares-2x2" class="w-4 h-4 text-pink-400 mt-1" aria-hidden="true" />
              <div class="flex-1">
                <div class="flex flex-wrap gap-1.5">
                  <Link
                    v-for="category in visibleCategories"
                    :key="category.id"
                    :href="`/tools/${category.slug}`"
                    class="text-xs text-gray-300 bg-gray-800/50 px-2 py-1 rounded-md"
                  >
                    {{ category.name }}
                  </Link>
                  <button 
                    v-if="remainingCategoriesCount > 0"
                    @click="showAllCategories = !showAllCategories"
                    class="text-xs text-blue-400 bg-blue-900/20 px-2 py-1 rounded-md flex items-center gap-1"
                  >
                    +{{ remainingCategoriesCount }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Related Tags -->
            <div v-if="tool.tags && tool.tags.length" class="flex items-start gap-2">
              <Icon icon="heroicons:tag" class="w-4 h-4 text-gray-400 mt-1" aria-hidden="true" />
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="tag in tool.tags.slice(0, 5)"
                  :key="tag.id"
                  class="text-xs text-gray-400"
                >
                  {{ tag.name }}{{ tag !== tool.tags.slice(0, 5)[tool.tags.slice(0, 5).length - 1] ? ',' : '' }}
                </span>
                <span v-if="tool.tags.length > 5" class="text-xs text-gray-500">
                  and {{ tool.tags.length - 5 }} more
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Layout -->
      <div class="hidden md:block">
        <div class="p-6 border-b border-gray-800">
          <div class="flex gap-6 items-start">
            <!-- Logo Column -->
            <div class="w-48 shrink-0">
              <div class="aspect-square w-full bg-gray-800/50 rounded-xl p-6 flex items-center justify-center">
                <img 
                  :src="toolImage" 
                  :alt="`${tool.name} logo`"
                  class="w-full h-full object-contain"
                  @error="onImageError"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            </div>

            <!-- Primary Info Column -->
            <div class="flex-1 min-w-0">
              <div class="space-y-3">
                <h1 class="text-4xl font-bold text-white">{{ tool.name }}</h1>
                
                <!-- Tags - loaded after critical content -->
                <div v-if="showTags" class="flex flex-wrap gap-2">
                  <span
                    v-for="tag in tool.tags?.slice(0, 3)"
                    :key="tag.id"
                    class="px-3 py-1 bg-gray-800/50 text-gray-300 text-sm rounded-full flex items-center gap-1.5"
                  >
                    <Icon icon="heroicons:hashtag" class="w-3.5 h-3.5" aria-hidden="true" />
                    {{ tag.name }}
                  </span>
                </div>
              </div>

              <div class="mt-3 text-lg text-gray-300">
                {{ tool.short_description }}
              </div>

              <div class="mt-4 space-y-3">
                <!-- Price and Platform Row -->
                <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <!-- Multiple Pricing Models -->
                  <div class="flex items-center gap-2">
                    <Icon icon="heroicons:currency-dollar" class="w-5 h-5 text-blue-400" aria-hidden="true" />
                    <div class="flex flex-wrap items-center gap-x-2">
                      <span class="text-gray-300 capitalize">{{ tool.pricing_model || 'Not specified' }}</span>
                    </div>
                  </div>

                  <!-- Multiple Platforms -->
                  <div v-if="tool.platforms && tool.platforms.length" class="flex items-center gap-2">
                    <Icon icon="heroicons:computer-desktop" class="w-5 h-5 text-purple-400" aria-hidden="true" />
                    <span class="text-gray-300">
                      {{ tool.platforms.map(p => p.name).join(', ') }}
                    </span>
                  </div>
                </div>

                <!-- Categories - lazy loaded -->
                <div v-if="showCategories && tool.use_cases && tool.use_cases.length" class="flex items-start gap-2">
                  <Icon icon="heroicons:squares-2x2" class="w-5 h-5 text-pink-400 mt-1.5" aria-hidden="true" />
                  <div class="flex-1">
                    <div class="flex flex-wrap gap-1.5">
                      <Link
                        v-for="category in visibleCategories"
                        :key="category.id"
                        :href="`/tools/${category.slug}`"
                        class="inline-flex items-center px-2.5 py-1 rounded-md text-sm 
                              bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600
                              text-gray-300 hover:text-white transition-colors"
                      >
                        {{ category.name }}
                      </Link>
                      <button 
                        v-if="remainingCategoriesCount > 0"
                        @click="showAllCategories = !showAllCategories"
                        class="inline-flex items-center px-2 py-1 rounded-md text-sm
                              bg-gradient-to-r from-blue-900/50 to-blue-800/50 
                              hover:from-blue-800/50 hover:to-blue-700/50
                              text-blue-400 hover:text-blue-300 transition-all gap-1"
                      >
                        <span>+{{ remainingCategoriesCount }}</span>
                        <Icon 
                          :icon="showAllCategories ? 'heroicons:chevron-up' : 'heroicons:chevron-down'" 
                          class="w-3.5 h-3.5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="shrink-0 flex flex-col gap-3">
              <a 
                :href="`/redirect/${tool.id}/tool_page/action`"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center justify-center gap-2 px-6 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-500 hover:to-blue-600 text-white py-2 px-4 rounded-lg transition-all duration-300"
              >
                <Icon icon="heroicons:globe-alt" class="w-5 h-5" aria-hidden="true" />
                Visit Website
              </a>
              
              <!-- Compare Button -->
              <button 
                @click="toggleInComparison"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-2 rounded-lg transition-all duration-300 border',
                  isInCompare ? 
                    'bg-purple-600 hover:bg-purple-700 text-white border-purple-500' : 
                    'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700 hover:text-white'
                ]"
              >
                <Icon 
                  :icon="isInCompare ? 'heroicons:square-2-stack-solid' : 'heroicons:square-2-stack'" 
                  class="w-5 h-5" 
                  aria-hidden="true"
                />
                <span>{{ isInCompare ? 'Added to Compare' : 'Add to Compare' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Description Section with Optimized Styling -->
      <section class="p-4 sm:p-6">        
        <div 
          class="prose prose-sm dark:prose-invert prose-headings:font-bold 
                prose-headings:mt-4 prose-headings:mb-2 prose-headings:text-white
                prose-p:my-2 prose-p:leading-relaxed prose-p:text-white
                prose-li:my-0.5 prose-li:leading-relaxed prose-li:text-white
                prose-a:text-blue-500 hover:prose-a:text-blue-400
                max-w-none rich-text-content"
          v-html="sanitizedDescription"
        ></div>
      </section>
      
      <!-- Notification for compare action feedback -->
      <transition name="fade">
        <div 
          v-if="showNotification" 
          class="fixed bottom-16 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg z-50"
        >
          {{ notificationText }}
        </div>
      </transition>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link } from '@inertiajs/vue3';
import { Icon } from '@iconify/vue';
import DOMPurify from 'dompurify';

const props = defineProps({
  tool: {
    type: Object,
    required: true,
    validator: (tool) => {
      return tool.id && typeof tool.name === 'string';
    }
  }
});

// Local state
const showAllCategories = ref(false);
const showNotification = ref(false);
const notificationText = ref('');

// Lazy load UI parts
const showAdditionalInfo = ref(false);
const showTags = ref(false);
const showCategories = ref(false);

// Import and initialize compare store with standard module import
// to avoid dynamic imports which can be problematic
import { useCompareStore } from '@/store/compareModule';
const compareStore = useCompareStore();

// Check if this tool is in comparison
const isInCompare = computed(() => {
  return compareStore.isInCompare(props.tool.id);
});

// Toggle tool in comparison
const toggleInComparison = () => {
  if (isInCompare.value) {
    // Remove from comparison
    compareStore.removeTool(props.tool.id);
    showFeedback('Removed from comparison');
  } else {
    // Add to comparison
    if (!compareStore.isCompareFull) {
      const added = compareStore.addTool(props.tool);
      if (added) {
        showFeedback('Added to comparison');
      }
    } else {
      showFeedback(`Maximum of ${compareStore.maxTools} tools reached`);
    }
  }
};

// Show notification feedback
const showFeedback = (message) => {
  notificationText.value = message;
  showNotification.value = true;

setTimeout(() => {
    showNotification.value = false;
  }, 2000);
};

// Compute visible categories for better performance
const visibleCategories = computed(() => {
  if (!props.tool.use_cases) return [];
  return showAllCategories.value
    ? props.tool.use_cases
    : props.tool.use_cases.slice(0, 4);
});

const remainingCategoriesCount = computed(() => {
  if (!props.tool.use_cases) return 0;
  return Math.max(0, props.tool.use_cases.length - 4);
});

// Get optimized tool image with fallback
const toolImage = computed(() => {
  return props.tool.media?.[0]?.original_url || '/storage/no-logo.png';
});

// Handle image loading errors
const onImageError = (event) => {
  event.target.src = '/storage/no-logo.png';
};

// Sanitize description HTML for security
const sanitizedDescription = computed(() => {
  if (!props.tool.description) return '';
  return DOMPurify.sanitize(props.tool.description, {
    ADD_ATTR: ['target', 'rel'],
    ALLOWED_TAGS: [
      'p', 'a', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'br', 'span',
      'div', 'blockquote', 'pre', 'code'
    ]
  });
});

// Stagger loading of non-critical UI elements
onMounted(() => {
  // Load additional UI elements sequentially for better performance
  setTimeout(() => {
    showAdditionalInfo.value = true;
  }, 150);
  
  setTimeout(() => {
    showTags.value = true;
  }, 200);
  
  setTimeout(() => {
    showCategories.value = true;
  }, 250);
});
</script>

<style scoped>
.card-style {
  background: 
    radial-gradient(
      ellipse at center, 
      rgba(30, 41, 59, 0.5) 0%, 
      rgba(17, 24, 39, 0.7) 100%
    );
  backdrop-filter: blur(2px);
}

.prose-invert {
  --tw-prose-invert-body: theme('colors.gray.300');
  --tw-prose-invert-headings: theme('colors.white');
  --tw-prose-invert-links: theme('colors.blue.400');
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

/* Global styles to override prose defaults - add to your global CSS or scoped styles */
.prose-sm h2 {
  font-size: 1.25rem !important;
  margin-top: 1.5rem !important;
  margin-bottom: 0.75rem !important;
  color: white !important;
}

.prose-sm h3 {
  font-size: 1.1rem !important;
  margin-top: 1.25rem !important;
  margin-bottom: 0.5rem !important;
}

.prose-sm p {
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
  font-size: 0.9rem !important;
  line-height: 1.5 !important;
}

.prose-sm ul {
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.prose-sm li {
  margin-top: 0.25rem !important;
  margin-bottom: 0.25rem !important;
  font-size: 0.9rem !important;
}

.prose-sm li p {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* Ensure spacing between sections is reasonable */
.prose-sm > * + * {
  margin-top: 1rem !important;
}

/* Override all text in the rich text content to be white */
.prose p, .prose li, .prose span {
  color: white !important; /* Use !important to override any inline styles */
}

/* Ensure headings have proper contrast */
.prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
  color: white !important;
}

/* Target all text elements in rich-text-content to ensure proper contrast */
.rich-text-content h1,
.rich-text-content h2,
.rich-text-content h3,
.rich-text-content h4,
.rich-text-content h5,
.rich-text-content h6 {
  color: white !important;
}

.rich-text-content p,
.rich-text-content li,
.rich-text-content span,
.rich-text-content div,
.rich-text-content strong,
.rich-text-content em,
.rich-text-content b,
.rich-text-content i {
  color: white !important;
}

/* Ensure lists have proper contrast */
.rich-text-content ul,
.rich-text-content ol,
.rich-text-content dl {
  color: white !important;
}

/* Handle tables if present */
.rich-text-content table,
.rich-text-content th,
.rich-text-content td {
  color: white !important;
  border-color: #4b5563 !important;
}

/* Handle any blockquotes */
.rich-text-content blockquote {
  color: #e2e8f0 !important;
  border-color: #4b5563 !important;
}

/* Preserve link colors */
.rich-text-content a {
  color: #3b82f6 !important;
}
.rich-text-content a:hover {
  color: #60a5fa !important;
}
</style>